

import React, { useState, useRef, useLayoutEffect, useContext, useEffect } from "react";
import axios from '../axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext';

import { useMountEffect, useUpdateEffect, useIntersectionObserver, useLocalStorage } from 'primereact/hooks';
import moment from 'moment';
import UsersContext from "../Context/UsersContext";
import { classNames } from "primereact/utils";
import { Skeleton } from 'primereact/skeleton';
import AppContext from "../Context/AppContext";
import LayoutContext from "../Context/LayoutContext";
import UserAddModal from "../Modals/UserAddModal";
import UserDeleteModal from "../Modals/UserDeleteModal";
import UserEditModal from "../Modals/UserEditModal";

const Users = () => {
    const [users, setUsers] = useState([]);
    const [skeleton, setSkeleton] = useState(0);
    const [loading, setLoading] = useState(false);

    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);

    const [currentData, setCurrentData] = useState(null);

    const dataParams = useRef({ offset: 0, limit: 16, count: 0, searchText: "" })
    const lastElementRef = useRef(null);
    const lastElementVisible = useIntersectionObserver(lastElementRef);
    const scrollPosition = useRef(0);
    const networkController = useRef(null)
    const scrollRef = useRef(null)

    const { setNavbar } = useContext(LayoutContext)
    const { isDesktop } = useContext(AppContext)

    const [searchText, setSearchText] = useLocalStorage("", "security.users.search")

    useEffect(() => {
        setNavbar(
            <div className='flex gap-2'>
                <div className="p-inputgroup max-w-20rem flex-1">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-search"></i>
                    </span>
                    <InputText placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <Button className="white-space-nowrap" label={"Add User"} onClick={() => setAddModal(true)} />
            </div>
        )
    }, [searchText])

    useMountEffect(() => {
        fetchData({ ...dataParams.current }, true);
    });

    useUpdateEffect(() => {
        if (!lastElementVisible)
            return;

        fetchData({ ...dataParams.current }, true)
    }, [lastElementVisible])

    useUpdateEffect(() => {
        dataParams.current.searchText = searchText

        fetchData({ ...dataParams.current, offset: 0 }, skeleton !== 0)
    }, [searchText]);

    const fetchData = async (params, skeleton = false) => {
        try {
            networkController.current?.abort()
            skeleton ? setSkeleton(params.limit) : setLoading(true)

            networkController.current = new AbortController()
            const response = await axios.get('/users', { signal: networkController.current.signal, params: params });

            const _users = [...users.slice(0, params.offset), ...response.data.data]
            setUsers(_users)

            dataParams.current = { ...dataParams.current, count: response.data.count, offset: params.offset + response.data.data.length }

            setSkeleton(0)
            setLoading(false)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                console.log("Request Canceled!");
            } else {
                console.error('Error:', error);
            }
        }
    };

    const registrationDateTemplate = (value) => {
        return value ? moment(value.registration_date).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const lastLoginDateTemplate = (value) => {
        return value ? moment(value.last_login).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const tagTemplate = (value) => {
        return value ? <Tag className='px-2' value={value.tag.toUpperCase()} style={{ color: '#' + value.foreground, backgroundColor: '#' + value.background }} rounded></Tag> : <Skeleton width="4rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const indexTemplate = (value, props) => {
        return value ? props.rowIndex + 1 : <Skeleton width="2rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const permissionsTemplate = (value, props) => {
        if (!value)
            return <Skeleton width="40rem" height="26px" borderRadius="16px"></Skeleton>

        const permissions = {
            admin: false,
            hidden: false,
            canAddImage: false,
            canSeeAllImages: false,
            canEditAllImages: false,
            canDeleteAllImages: false,
            canAddVideo: false,
            canSeeAllVideos: false,
            canEditAllVideos: false,
            canDeleteAllVideos: false,
            canAddEmbed: false,
            canSeeAllEmbeds: false,
            canEditAllEmbeds: false,
            canDeleteAllEmbeds: false,
            canAddPlaylist: false,
            canSeeAllPlaylists: false,
            canEditAllPlaylists: false,
            canDeleteAllPlaylists: false,
            canAddDisplay: false,
            canSeeAllDisplays: false,
            canEditAllDisplays: false,
            canDeleteAllDisplays: false,
        }

        Object.keys(permissions).forEach((key, i) => {
            permissions[key] = (value.permissions & (1 << i)) !== 0;
        });

        return (
            <div className="flex gap-3">
                <div className="flex align-items-center">
                    <Checkbox checked={permissions.admin} readOnly />
                    <label className="ml-2">Admin</label>
                </div>
            </div>
        );
    }

    const nameTemplate = (value) => {
        return value ? value.user : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const actionTemplate = (value) => {
        return value ?
            <div className="flex gap-2">
                <Button className="p-1" style={{ width: '2.25rem', height: '2.25rem' }} icon="pi pi-pencil" rounded outlined aria-label="Filter" onClick={() => { setCurrentData(value); setEditModal(true) }} />
                <Button className="p-1" style={{ width: '2.25rem', height: '2.25rem' }} icon="pi pi-trash" rounded outlined aria-label="Filter" severity="danger" onClick={() => { setCurrentData(value); setDeleteModal(true) }} />
            </div>
            : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const action = (value) => {
        return value ?
            <div className='flex gap-2 justify-content-end'>
                {value.permissions === 1 && <i className="pi pi-key text-xl p-1 icon-button" style={{ color: 'var(--green-500)' }} onClick={() => { setCurrentData(value); setEditModal(true) }}></i>}
                <i className="pi pi-pencil text-xl p-1 icon-button" style={{ color: 'var(--primary-color)' }} onClick={() => { setCurrentData(value); setEditModal(true) }}></i>
                <i className="pi pi-trash text-xl p-1 icon-button" style={{ color: 'var(--red-500)' }} onClick={() => { setCurrentData(value); setDeleteModal(true) }}></i>
            </div>
            : "";
    }

    useLayoutEffect(() => {
        scrollRef.current.scrollTo({
            top: scrollPosition.current,
            left: 0,
            behavior: "instant",
        });
    }, [users]);

    const handleScroll = (event) => {
        scrollPosition.current = event.target.scrollTop
    }

    return (
        <UsersContext.Provider value={{ setAddModal }}>
            <div className="flex flex-column flex-auto surface-section overflow-scroll align-items-center" ref={scrollRef} onScroll={handleScroll}>
                <div className="w-full datacontent flex-1 container relative px-0 md:px-4">
                    <DataTable size={"small"} value={users.concat(Array(skeleton).fill(null))} className={classNames("users-table surface-ground white-space-nowrap", loading && !skeleton ? "opacity-50" : "")}>
                        <Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }} bodyStyle={{maxWidth: "0px", minWidth: "min-content"}}></Column>
                        <Column field="name" header="Name" body={nameTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                        {isDesktop && <Column field="registration_date" header="Registration" body={registrationDateTemplate} bodyStyle={{maxWidth: "0px"}}></Column>}
                        <Column field="last_login" header="Last Login" body={lastLoginDateTemplate} bodyStyle={{ whiteSpace: "nowrap", maxWidth: "0px", overflow: "hidden", textOverflow: "ellipsis" }}></Column>
                        <Column bodyStyle={{maxWidth: "0px"}} headerStyle={{ width: '100px', textAlign: 'center' }} body={action} />
                    </DataTable>
                    {(!loading && skeleton === 0) && <div className={classNames("absolute bottom-0 w-1rem h-20rem", lastElementVisible || dataParams.current.count === dataParams.current.offset ? "hidden" : "")} ref={lastElementRef}></div>}
                </div>
            </div>

            {addModal && <UserAddModal hide={() => setAddModal(false)} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset + 1 })
            }}></UserAddModal>}
            {editModal && <UserEditModal hide={() => setEditModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserEditModal>}
            {deleteModal && <UserDeleteModal hide={() => setDeleteModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserDeleteModal>}
        </UsersContext.Provider>
    );
}

/**
<Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }}></Column>
                        <Column field="name" header="Name" body={nameTemplate} ></Column>
                        {isDesktop && <Column field="registration_date" header="Registration" body={registrationDateTemplate} ></Column>}
 */

/*
{addModal && <UserAddModal hide={() => setAddModal(false)} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserAddModal>}
            {editModal && <UserEditModal hide={() => setEditModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserEditModal>}
            {deleteModal && <UserDeleteModal hide={() => setDeleteModal(false)} data={currentData} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset })
            }}></UserDeleteModal>}
*/

export default Users;